import React, {
  forwardRef,
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'

import { cx } from '@linaria/core'
import { easings, useSpring, useSprings } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'

import { VoidHandler } from 'common/types'
import { MAIN_LAYOUT_ID } from 'components/designSystem/layout/WaveLayout/WaveLayoutMainContainer'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { TrailMemo, WrapperTrails } from './block/Trail/Trail'
import { CanvasCurveMemo } from './canvas/Canvas'
import { activeCardCss, CardParentMemo } from './Card.styled'
import { stopRotate } from './function/stopRotate'
import { Direction } from './Rating.types'
import { RATING_LAYOUT_ID } from './RatingQueue'

const ROTATE_FACTOR = 40
const TRAIL_COUNT = 3

export type DraggableRef = {
  move: (action: Direction) => void
}

/**
 * disableTrail?: boolean - отключаем шлейф для notice
 * hasChangeBodyBackground?: boolean - отключаем смену цвет заднего фона для notice
 */
const RatingDragDrop = forwardRef<
  DraggableRef,
  PropsWithChildren<{
    onHandleEventEnd: VoidHandler
    onHandleEventRollback?: VoidHandler
    enable: boolean
    hasChangeBodyBackground?: boolean
    rollbackPosition?: Direction | 0
    disableTrail?: boolean
  }>
>(
  (
    {
      children,
      enable,
      onHandleEventEnd,
      onHandleEventRollback,
      hasChangeBodyBackground = true,
      rollbackPosition = 0,
      disableTrail = false,
    },
    ref
  ) => {
    const { mobile, rtl } = useShallowEqualSelector(
      ({ systemView: { mobile }, systemReducer: { rtl, touch } }) => ({
        mobile,
        rtl,
        touch,
      })
    )
    const cardOutOfBoundsRef = useRef<Direction | null>(null)
    const currentCardRef = useRef<HTMLDivElement>(null)
    const requestAnimationFrameRef = useRef<number>(0)

    const disabledTrail = useMemo(
      () => !enable && disableTrail && !mobile,
      [enable, mobile, disableTrail]
    )

    const isTouch = useMemo(
      () => 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      []
    )

    const mainLayoutWidth = useCallback(
      () => document.getElementById(MAIN_LAYOUT_ID)?.offsetWidth || 0,
      []
    )
    const contentLayoutWidth = useCallback(
      () => document.getElementById(RATING_LAYOUT_ID)?.offsetWidth || 0,
      []
    )

    const computeDistance = useCallback(() => {
      const _mainLayoutWidth = mainLayoutWidth()
      const _contentLayoutWidth = contentLayoutWidth()

      const cardWidth = mobile ? _mainLayoutWidth : _contentLayoutWidth

      const movingDistance = (window.innerWidth - _mainLayoutWidth) / 2
      const sidebarWidth = _mainLayoutWidth - cardWidth

      const translate = cardWidth + movingDistance

      return {
        translate,
        sidebarWidth,
      }
    }, [contentLayoutWidth, mainLayoutWidth, mobile])

    const computeSlideDistance = useCallback(
      (direction: Direction | 0) => {
        if (direction !== 0) {
          const { translate, sidebarWidth } = computeDistance()
          const translateX = translate * direction

          if (rtl && direction === Direction.right) {
            return translateX + sidebarWidth
          }

          if (!rtl && direction === Direction.left) {
            return translateX - sidebarWidth
          }

          return translateX
        }

        return 0
      },
      [computeDistance, rtl]
    )

    const [{ x, rotate }, api] = useSpring<{
      x: number
      rotate: number
    }>(() => ({
      x: 0,
      rotate: 0,
      active: false,
      onRest: () => {
        if (cardOutOfBoundsRef.current) {
          onHandleEventEnd(cardOutOfBoundsRef.current)
        }

        if (rollbackPosition) {
          onHandleEventRollback?.()
        }
      },
      // использовать для возвращения
      from: {
        x: computeSlideDistance(rollbackPosition),
        rotate: ROTATE_FACTOR * rollbackPosition,
      },
      config: {
        easing: easings.linear,
      },
    }))

    const [trailProps, trailApi] = useSprings(TRAIL_COUNT, (i) => ({
      x: 0,
      rotate: 0,
      config: {
        friction: 22 - i,
        tension: 90 + i * 10,
      },
      from: {
        x: computeSlideDistance(rollbackPosition),
        rotate: ROTATE_FACTOR * rollbackPosition,
      },
    }))

    const moveOutOrBack = (trigger: boolean, movementX: number) => {
      if (trigger) {
        const direction = movementX > 0 ? Direction.right : Direction.left

        cardOutOfBoundsRef.current = direction
        if (currentCardRef.current) {
          currentCardRef.current.style.pointerEvents = 'none'
        }

        const translateX = computeSlideDistance(direction)

        return {
          x: translateX,
          rotate: stopRotate(movementX / ROTATE_FACTOR),
          config: {
            easing: easings.linear,
          },
        }
      }

      return {
        x: 0,
        rotate: 0,
        direction: 0,
      }
    }

    const moving = (movementX: number) => ({
      x: movementX,
      rotate: stopRotate(movementX / ROTATE_FACTOR),
    })

    const bind = useDrag(
      ({
        movement: [movementX],
        active,
        swipe: [swipeX],
        velocity: [velocityX],
      }) => {
        if (enable && !cardOutOfBoundsRef.current && movementX !== 0) {
          requestAnimationFrameRef.current = requestAnimationFrame(() => {
            const swipeTrigger = swipeX != 0 && velocityX > 0.2

            const currentWidth = contentLayoutWidth()
            const boundsTrigger =
              currentWidth !== 0 && currentWidth / 4 < Math.abs(movementX)

            const trigger = !active && (swipeTrigger || boundsTrigger)

            const state = active
              ? moving(movementX)
              : moveOutOrBack(trigger, movementX)

            api.start({
              ...state,
            })

            if (!disabledTrail) {
              trailApi.start((i) => ({
                ...state,
                config: {
                  friction: 20 - i,
                  tension: 75 + i * 10,
                },
              }))
            }
          })
        }
      },
      {
        delay: true,
        preventScroll: true,
        preventScrollAxis: 'y',
        pointer: {
          buttons: [0, 1],
          mouse: true,
          touch: true,
        },
        filterTaps: !isTouch,
        triggerAllEvents: true,
      }
    )

    useImperativeHandle(ref, () => {
      const handleMove = (direction: Direction) => {
        if (enable) {
          requestAnimationFrameRef.current = requestAnimationFrame(() => {
            const translateX = computeSlideDistance(direction)

            cardOutOfBoundsRef.current = direction

            if (currentCardRef && currentCardRef.current) {
              currentCardRef.current.style.pointerEvents = 'none'
            }

            const state = {
              x: translateX,
              rotate: stopRotate(translateX / ROTATE_FACTOR),
            }

            api.start({
              ...state,
              config: {
                easing: easings.linear,
              },
            })

            if (!disabledTrail) {
              trailApi.start((i) => ({
                ...state,
                config: {
                  friction: 22 - i,
                  tension: 90 + i * 10,
                },
              }))
            }
          })
        }
      }

      return {
        move: (direction: Direction) => handleMove(direction),
      }
    })

    const direction = useMemo(() => {
      return x.to((x) => {
        if (x > 0) {
          return 'like'
        } else if (x < 0) {
          return 'dislike'
        } else {
          return 'neutral'
        }
      })
    }, [x])

    useEffect(() => {
      return () => {
        cancelAnimationFrame(requestAnimationFrameRef.current)
      }
    }, [])

    return (
      <>
        {enable && !disableTrail && !mobile && (
          <WrapperTrails data-name={direction}>
            {trailProps.map(({ x, rotate }, i) => (
              <TrailMemo key={i} bind={bind} x={x} rotate={rotate} />
            ))}
          </WrapperTrails>
        )}
        {enable && mobile && (
          <CanvasCurveMemo
            x={x}
            _rotate={rotate}
            hasChangeBodyBackground={hasChangeBodyBackground}
          />
        )}
        <CardParentMemo
          ref={currentCardRef}
          style={{ x, rotate }}
          className={cx(enable && activeCardCss)}
          {...bind()}
        >
          {children}
        </CardParentMemo>
      </>
    )
  }
)

export const RatingDragDropMemo = memo(RatingDragDrop)
